// Excel exports — reproduce the G&L One Week-4 workbook: dated daily tabs with per-shift
// worker/product/tally/balance breakdowns, Weekly Summary, Weekly Product Tally, KPI.
// Colours: Navy #1F3864 headers · Blue #DDEBF7 typed · Green #E2EFDA auto · Yellow #FFF2CC check.
import ExcelJS from "exceljs";
import api from "@/lib/api";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";
const photoUrl = (p) => `${API_BASE}/api/files/view/${p.id}`;
function linkCell(cell, text, url) { cell.value = { text, hyperlink: url }; cell.font = { color: { argb: "FF0563C1" }, underline: true, size: 10 }; }

const NAVY = "FF1F3864", BLUE = "FFDDEBF7", GREEN = "FFE2EFDA", YELLOW = "FFFFF2CC";
const NUM = "#,##0.00", MONEY = "$#,##0.00";
const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const DAYFULL = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const SHIFT_LABEL = { night: "1st Shift (Night Shift)", day: "2nd Shift (Day Shift)", afternoon: "3rd Shift (Afternoon Shift)", pooraka_day: "Pooraka Day Shift" };

const parse = (iso) => new Date(iso + "T12:00:00");
const dow = (iso) => parse(iso).getDay();
const ddmm = (iso) => { const d = parse(iso); return `${String(d.getDate()).padStart(2, "0")}-${String(d.getMonth() + 1).padStart(2, "0")}`; };
const shortDay = (iso) => `${DAYS[dow(iso)]} ${ddmm(iso)}`;
const SHIFT_SHORT = { night: "1st", day: "2nd", afternoon: "3rd", pooraka_day: "Pooraka" };

function sundayOf(iso) { const d = parse(iso); d.setDate(d.getDate() - d.getDay()); return d.toISOString().slice(0, 10); }
function weekDatesFrom(entries) {
  const dates = entries.map((e) => e.date).filter(Boolean).sort();
  const start = dates.length ? sundayOf(dates[0]) : sundayOf(new Date().toISOString().slice(0, 10));
  return Array.from({ length: 7 }, (_, i) => { const d = parse(start); d.setDate(d.getDate() + i); return d.toISOString().slice(0, 10); });
}

function head(row) { row.eachCell((c) => { c.fill = { type: "pattern", pattern: "solid", fgColor: { argb: NAVY } }; c.font = { bold: true, color: { argb: "FFFFFFFF" } }; c.alignment = { vertical: "middle" }; }); }
function title(cell) { cell.font = { bold: true, size: 14 }; }
function section(cell) { cell.font = { bold: true, size: 11 }; }
function fill(cell, argb, num) { cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb } }; if (num) cell.numFmt = num; }
function statusCell(cell, status) { cell.font = { bold: true }; cell.alignment = { horizontal: "center" }; fill(cell, status === "BALANCED" ? GREEN : YELLOW); }

const num = (n) => Math.round((Number(n) || 0) * 100) / 100;

async function addDailySheet(wb, date, entries, week, money, embedPhotos = false) {
  const day = DAYFULL[dow(date)];
  const dayEntries = entries.filter((e) => e.date === date);
  const ws = wb.addWorksheet(`Daily Entry - ${shortDay(date)}`);
  let r = 1;
  ws.getCell(`A${r}`).value = `DAILY ENTRY — ${day.toUpperCase()} · WEEK ${week}`; title(ws.getCell(`A${r}`)); ws.getCell(`E${r}`).value = date; r += 1;
  ws.getCell(`A${r}`).value = "Green cells are LIVE FORMULAS — click a green cell to trace the maths. Blue = typed on the floor."; ws.getCell(`A${r}`).font = { italic: true, size: 9 }; r += 1;
  const bwVal = num(dayEntries.find((e) => e.bucket_weight)?.bucket_weight) || 10;
  ws.getCell(`A${r}`).value = "Bucket multiplier — kg per bucket (editable; e.g. 9). KG Earned = Buckets × this cell:"; ws.getCell(`A${r}`).font = { bold: true, size: 9 };
  ws.getCell(`B${r}`).value = bwVal; fill(ws.getCell(`B${r}`), BLUE, NUM);
  const bwCell = `$B$${r}`; r += 2;

  if (!dayEntries.length) { ws.getCell(`A${r}`).value = "No shifts filed for this day."; ws.getCell(`A${r}`).font = { italic: true, color: { argb: "FF999999" } }; }

  const blockWorkerTot = [];
  const blockAllocTot = [];

  for (const e of dayEntries) {
    ws.getCell(`A${r}`).value = `${e.line} — ${SHIFT_LABEL[e.shift] || e.shift}`; section(ws.getCell(`A${r}`));
    ws.getCell(`E${r}`).value = e.status; statusCell(ws.getCell(`E${r}`), e.status); r++;
    ws.getCell(`A${r}`).value = `Logged by ${e.saved_by_name || "—"}${e.saved_at ? " · " + new Date(e.saved_at).toLocaleString("en-AU") : ""}`;
    ws.getCell(`A${r}`).font = { italic: true, size: 9, color: { argb: "FF777777" } }; r++;

    const hasMin = (e.workers || []).some((w) => w.type === "pool");
    let poolCell = null, prodValRow = null;
    if (hasMin) {
      ws.getCell(`A${r}`).value = "Weighed pool kg:"; ws.getCell(`A${r}`).font = { bold: true, size: 9 };
      ws.getCell(`B${r}`).value = num(e.pool_kgs); fill(ws.getCell(`B${r}`), BLUE, NUM);
      poolCell = `$B$${r}`; r++;
    }
    if (money) {
      ws.getCell(`A${r}`).value = "Shift performance $ (= KG × product rate):"; ws.getCell(`A${r}`).font = { bold: true, size: 9 };
      ws.getCell(`B${r}`).value = num(e.production_value); fill(ws.getCell(`B${r}`), YELLOW, MONEY);
      prodValRow = r; r++;
    }

    // Worker entries
    const wh = ws.getRow(r);
    wh.values = money
      ? ["No.", "Worker", "Method", "Minutes", "Buckets", "Direct KG", "Pieces", "KG Earned", "Value $"]
      : ["No.", "Worker", "Method", "Minutes", "Buckets", "Direct KG", "Pieces", "KG Earned"];
    head(wh); r++;
    const firstW = r;
    const lastW = firstW + Math.max(0, (e.workers || []).length) - 1;
    const workerRows = [];
    (e.workers || []).forEach((w, i) => {
      const row = ws.getRow(r);
      row.getCell(1).value = w.no ?? (i + 1);
      row.getCell(2).value = w.name;
      row.getCell(3).value = w.type;
      row.getCell(4).value = w.minutes ?? "";
      row.getCell(5).value = w.buckets ?? "";
      row.getCell(6).value = w.direct_kg ?? "";
      row.getCell(7).value = w.pieces ?? "";
      let f = "0";
      if (w.type === "buckets") f = `E${r}*${bwCell}`;
      else if (w.type === "direct") f = `F${r}`;
      else if (w.type === "pool" && poolCell) f = `IFERROR((D${r}/SUM(D${firstW}:D${lastW}))*${poolCell},0)`;
      row.getCell(8).value = { formula: f, result: num(w.earned_kg) };
      if (w.minutes != null && w.minutes !== "") fill(row.getCell(4), BLUE, "0");
      if (w.buckets != null && w.buckets !== "") fill(row.getCell(5), BLUE, "0");
      if (w.direct_kg != null && w.direct_kg !== "") fill(row.getCell(6), BLUE, NUM);
      fill(row.getCell(8), GREEN, NUM);
      if (money) fill(row.getCell(9), GREEN, MONEY);
      workerRows.push({ row: r, val: num(w.value), no: w.no, name: w.name });
      r++;
    });
    const totRow = r;
    const tot = ws.getRow(r);
    tot.getCell(2).value = "TOTAL";
    tot.getCell(8).value = firstW <= lastW ? { formula: `SUM(H${firstW}:H${lastW})`, result: num(e.total_worked) } : 0;
    tot.font = { bold: true }; fill(tot.getCell(8), GREEN, NUM);
    if (money) { tot.getCell(9).value = firstW <= lastW ? { formula: `SUM(I${firstW}:I${lastW})`, result: num(e.production_value) } : 0; fill(tot.getCell(9), GREEN, MONEY); }
    blockWorkerTot.push(`H${totRow}`); r += 2;

    // Product allocation
    const ah = ws.getRow(r);
    ah.values = money ? ["Product code", "KG allocated", "Rate $/kg", "Value $"] : ["Product code", "KG allocated"];
    head(ah); r++;
    const firstA = r;
    (e.allocations || []).forEach((a) => {
      const row = ws.getRow(r);
      row.getCell(1).value = a.reason ? `Reason: ${a.reason}` : a.code;
      row.getCell(2).value = num(a.kg); fill(row.getCell(2), BLUE, NUM);
      if (money) {
        const rate = num(a.kg) > 0 && a.value != null ? num(a.value) / num(a.kg) : 0;
        row.getCell(3).value = num(rate); fill(row.getCell(3), BLUE, MONEY);
        row.getCell(4).value = { formula: `B${r}*C${r}`, result: num(a.value) }; fill(row.getCell(4), GREEN, MONEY);
      }
      r++;
    });
    const lastA = r - 1;
    const allocTotRow = r;
    const ab = ws.getRow(r);
    ab.getCell(1).value = "ALLOCATED TOTAL";
    ab.getCell(2).value = firstA <= lastA ? { formula: `SUM(B${firstA}:B${lastA})`, result: num(e.total_allocated) } : 0;
    ab.font = { bold: true }; fill(ab.getCell(2), GREEN, NUM);
    if (money) { ab.getCell(4).value = firstA <= lastA ? { formula: `SUM(D${firstA}:D${lastA})`, result: num(e.production_value) } : 0; fill(ab.getCell(4), GREEN, MONEY); }
    blockAllocTot.push(`B${allocTotRow}`); r++;
    // Wire each worker's Value $ to the editable product Rate cells: Value = KG Earned × blended $/kg.
    // Single-product shift → exactly KG × that product's rate; multi-product → shift blended rate.
    // Edit any Rate $/kg cell below and every worker $, the worker total and the product value recalc.
    if (money) {
      const blended = `IFERROR($D$${allocTotRow}/$B$${allocTotRow},0)`;
      workerRows.forEach(({ row: wr, val }) => { ws.getCell(`I${wr}`).value = { formula: `H${wr}*${blended}`, result: val }; });
      if (prodValRow) ws.getCell(`B${prodValRow}`).value = { formula: `D${allocTotRow}`, result: num(e.production_value) };
    }
    // Record live cell addresses so the Weekly Pay / Summary / per-worker sheets can point back
    // here (edits on this daily sheet — rates, kilos, minutes, buckets, a worker's pay — flow
    // straight through to the weekly totals, the summary and each worker's performance tab).
    e.__cells = {
      sheet: ws.name,
      shiftKg: `B${allocTotRow}`,
      shiftDollar: money ? `D${allocTotRow}` : null,
      workers: workerRows.map((wv) => ({ no: wv.no, name: wv.name, value: `I${wv.row}`, kg: `H${wv.row}`, minutes: `D${wv.row}`, buckets: `E${wv.row}` })),
    };
    // Balance row — worker earned total minus allocated total (live formula)
    const bal = ws.getRow(r);
    bal.getCell(1).value = "BALANCE (earned − allocated)";
    bal.getCell(2).value = { formula: `H${totRow}-B${allocTotRow}`, result: num((e.total_worked || 0) - (e.total_allocated || 0)) };
    fill(bal.getCell(2), YELLOW, NUM);
    bal.getCell(3).value = e.status; statusCell(bal.getCell(3), e.status);
    bal.font = { bold: true }; r += 2;

    // Per-shift photos — links (light, default) or embedded thumbnails (full file)
    if ((e.photos || []).length) {
      ws.getCell(`A${r}`).value = "Photos (evidence):"; ws.getCell(`A${r}`).font = { bold: true, size: 9 };
      if (embedPhotos) {
        const startRow = r; let col = 2;
        for (const p of e.photos) {
          try {
            const res = await api.get(`/files/${p.storage_path}`, { responseType: "arraybuffer" });
            const ext0 = (p.storage_path.split(".").pop() || "jpeg").toLowerCase();
            const extension = ext0 === "jpg" ? "jpeg" : (["png", "gif", "jpeg"].includes(ext0) ? ext0 : "jpeg");
            const imageId = wb.addImage({ buffer: res.data, extension });
            ws.addImage(imageId, { tl: { col, row: startRow }, ext: { width: 120, height: 90 } });
            col += 2;
          } catch (err) { /* skip unavailable / non-image photo */ }
        }
        for (let k = 0; k < 6; k++) ws.getRow(startRow + k).height = 15;
        r = startRow + 7;
      } else {
        e.photos.forEach((p, i) => {
          linkCell(ws.getCell(`B${r}`), `📷 ${p.original_filename || p.filename || `Photo ${i + 1}`}`, photoUrl(p));
          r++;
        });
        r += 1;
      }
    }
  }

  // Product tally for the day (aggregate reference)
  const tally = {};
  dayEntries.forEach((e) => (e.allocations || []).forEach((a) => { tally[a.code] = (tally[a.code] || 0) + (Number(a.kg) || 0); }));
  if (Object.keys(tally).length) {
    ws.getCell(`A${r}`).value = "PRODUCT TALLY (day totals by product)"; section(ws.getCell(`A${r}`)); r++;
    const th = ws.getRow(r); th.values = ["Product code", "Day total KG"]; head(th); r++;
    Object.entries(tally).sort((a, b) => b[1] - a[1]).forEach(([code, kg]) => {
      const row = ws.getRow(r); row.values = [code, num(kg)]; fill(row.getCell(2), GREEN, NUM); r++;
    });
    r++;
  }

  // Daily balance proof — Σ earned by workers vs Σ allocated to products (live formulas)
  const dayEarned = num(dayEntries.reduce((s, e) => s + (Number(e.total_worked) || 0), 0));
  const dayAllocated = num(dayEntries.reduce((s, e) => s + (Number(e.total_allocated) || 0), 0));
  const dayDiff = num(dayEarned - dayAllocated);
  const dayStatus = Math.abs(dayDiff) < 0.01 && dayEarned > 0 ? "BALANCED" : dayEarned === 0 ? "—" : "CHECK DATA";
  ws.getCell(`A${r}`).value = "DAILY BALANCE PROOF — kilos earned by workers must equal kilos allocated to products."; section(ws.getCell(`A${r}`)); r++;
  const bph = ws.getRow(r); bph.values = ["Check", "Worker KG (earned)", "Produced KG (allocated)", "Difference", "Status"]; head(bph); r++;
  const bprRow = r;
  const bpr = ws.getRow(r);
  bpr.getCell(1).value = "Whole day";
  bpr.getCell(2).value = blockWorkerTot.length ? { formula: blockWorkerTot.join("+"), result: dayEarned } : 0;
  bpr.getCell(3).value = blockAllocTot.length ? { formula: blockAllocTot.join("+"), result: dayAllocated } : 0;
  bpr.getCell(4).value = { formula: `B${bprRow}-C${bprRow}`, result: dayDiff };
  bpr.getCell(5).value = dayStatus;
  fill(bpr.getCell(2), GREEN, NUM); fill(bpr.getCell(3), GREEN, NUM); fill(bpr.getCell(4), YELLOW, NUM); statusCell(bpr.getCell(5), dayStatus);
  bpr.font = { bold: true }; r++;

  [26, 24, 12, 10, 10, 11, 9, 12, 12].forEach((w, i) => (ws.getColumn(i + 1).width = w));
}

function addWeeklySummary(wb, entries, dates, week, money) {
  const ws = wb.addWorksheet("Weekly Summary");
  ws.getCell("A1").value = `WEEKLY SUMMARY — WEEK ${week} (Sun ${ddmm(dates[0])} → Sat ${ddmm(dates[6])})`; title(ws.getCell("A1"));
  ws.getCell("A2").value = "Working week runs Sunday → Saturday. Earned kg per day per worker."; ws.getCell("A2").font = { italic: true, size: 9 };
  const h = ws.getRow(4);
  h.values = ["Worker", ...dates.map((d) => `${DAYS[dow(d)]} KG`), "Week Minutes", "Week Buckets", "Week KG", ...(money ? ["Week Value $"] : []), "Rank"];
  head(h);
  // Collect the LIVE daily-sheet cell addresses per worker (recorded by addDailySheet) so the
  // day KG / Week Minutes / Week Buckets cells here point straight back at the daily sheets —
  // exactly like the $ column already does. Edit a daily cell → these recalc.
  const refBy = new Map();
  entries.forEach((e) => {
    const c = e.__cells; if (!c || !c.sheet) return;
    const q = `'${c.sheet.replace(/'/g, "''")}'`;
    (c.workers || []).forEach((wc) => {
      if (!refBy.has(wc.name)) refBy.set(wc.name, { kg: {}, min: [], bkt: [], val: [] });
      const rec = refBy.get(wc.name);
      (rec.kg[e.date] = rec.kg[e.date] || []).push(`${q}!${wc.kg}`);
      rec.min.push(`${q}!${wc.minutes}`); rec.bkt.push(`${q}!${wc.buckets}`);
      if (wc.value) rec.val.push(`${q}!${wc.value}`);
    });
  });

  const by = new Map();
  entries.forEach((e) => (e.workers || []).forEach((w) => {
    const key = w.name; const di = dates.indexOf(e.date); if (di < 0) return;
    if (!by.has(key)) by.set(key, { kg: Array(7).fill(0), val: 0, minutes: 0, buckets: 0, no: w.no });
    const rec = by.get(key);
    if (rec.no == null && w.no != null) rec.no = w.no;
    rec.kg[di] += Number(w.earned_kg) || 0; rec.val += Number(w.value) || 0;
    rec.minutes += Number(w.minutes) || 0; rec.buckets += Number(w.buckets) || 0;
  }));
  const rows = [...by.entries()].map(([name, v]) => ({ name, ...v, total: v.kg.reduce((a, b) => a + b, 0) })).sort((a, b) => b.total - a.total);
  let r = 5;
  rows.forEach((row, i) => {
    const xr = ws.getRow(r);
    xr.getCell(1).value = row.no != null ? `#${row.no} ${row.name}` : row.name;
    const ref = refBy.get(row.name);
    // Day KG cells — live SUM of that worker's KG-earned cells on the matching daily sheet.
    row.kg.forEach((k, j) => {
      const cell = xr.getCell(2 + j);
      const refs = ref && ref.kg[dates[j]];
      if (refs && refs.length) { cell.value = { formula: `SUM(${refs.join(",")})`, result: num(k) }; fill(cell, GREEN, NUM); }
      else if (k) { cell.value = num(k); fill(cell, GREEN, NUM); }
      else cell.value = "";
    });
    // Week Minutes / Week Buckets — live sums of the daily minutes/buckets cells.
    if (ref && ref.min.length) xr.getCell(9).value = { formula: `SUM(${ref.min.join(",")})`, result: row.minutes || 0 };
    else xr.getCell(9).value = row.minutes || "";
    if (row.minutes) fill(xr.getCell(9), GREEN, "0");
    if (ref && ref.bkt.length) xr.getCell(10).value = { formula: `SUM(${ref.bkt.join(",")})`, result: row.buckets || 0 };
    else xr.getCell(10).value = row.buckets || "";
    if (row.buckets) fill(xr.getCell(10), GREEN, "0");
    // Week KG — sum of the (now live) day cells.
    fill(xr.getCell(11), GREEN, NUM); xr.getCell(11).font = { bold: true };
    xr.getCell(11).value = { formula: `SUM(B${r}:H${r})`, result: num(row.total) };
    if (money) {
      fill(xr.getCell(12), GREEN, MONEY);
      if (ref && ref.val.length) xr.getCell(12).value = { formula: `SUM(${ref.val.join(",")})`, result: num(row.val) };
      else { const nm = String(row.name).replace(/"/g, '""'); xr.getCell(12).value = { formula: `SUMIF('Weekly Pay Detail'!$F:$F,"${nm}",'Weekly Pay Detail'!$K:$K)`, result: num(row.val) }; }
    }
    xr.getCell(money ? 13 : 12).value = row.total ? i + 1 : "";
    r++;
  });
  ws.getColumn(1).width = 26; for (let c = 2; c <= 13; c++) ws.getColumn(c).width = 11;
}

function addWeeklyProductTally(wb, entries, dates, week) {
  const ws = wb.addWorksheet("Weekly Product Tally");
  ws.getCell("A1").value = `WEEKLY PRODUCT TALLY — WEEK ${week} (charge-out report)`; title(ws.getCell("A1"));
  const h = ws.getRow(3); h.values = ["Product code", ...dates.map((d) => DAYS[dow(d)]), "Week Total KG"]; head(h);
  const by = new Map();
  entries.forEach((e) => (e.allocations || []).forEach((a) => {
    const di = dates.indexOf(e.date); if (di < 0) return;
    if (!by.has(a.code)) by.set(a.code, Array(7).fill(0));
    by.get(a.code)[di] += Number(a.kg) || 0;
  }));
  let r = 4;
  [...by.entries()].sort((a, b) => b[1].reduce((x, y) => x + y, 0) - a[1].reduce((x, y) => x + y, 0)).forEach(([code, kgs]) => {
    const total = kgs.reduce((a, b) => a + b, 0); if (!total) return;
    const row = ws.getRow(r); row.values = [code, ...kgs.map((k) => (k ? num(k) : "")), num(total)];
    kgs.forEach((k, j) => { if (k) fill(row.getCell(2 + j), GREEN, NUM); });
    fill(row.getCell(9), GREEN, NUM); row.getCell(9).font = { bold: true };
    row.getCell(9).value = { formula: `SUM(B${r}:H${r})`, result: num(total) }; r++;
  });
  ws.getColumn(1).width = 36; for (let c = 2; c <= 9; c++) ws.getColumn(c).width = 11;
}

// Admin-only: $ each worker earned FOR EACH PRODUCT this week (worker performance rates,
// NOT charge-out). Matrix ('Weekly Pay') uses live SUMIFS into the traceable detail sheet.
function addWeeklyPay(wb, entries, dates, week) {
  const inWeek = new Set(dates);
  const wk = entries.filter((e) => inWeek.has(e.date));

  // One row per worker × SHIFT — every $ / KG cell points back to the live daily-sheet cell,
  // so changing a rate, a worker's kilos, or a worker's pay on a daily sheet flows straight through.
  const detail = [];
  wk.forEach((e) => {
    const cells = e.__cells || {};
    const q = cells.sheet ? `'${cells.sheet.replace(/'/g, "''")}'` : null;
    const wmap = {}; (cells.workers || []).forEach((wc) => { wmap[`${wc.no}|${wc.name}`] = wc; });
    const shiftKG = num(e.total_worked);
    const shiftVal = num(e.production_value);
    (e.workers || []).forEach((w) => {
      const workerKG = num(w.earned_kg);
      if (workerKG <= 0) return;
      const wc = wmap[`${w.no}|${w.name}`];
      const share = shiftKG > 0 ? workerKG / shiftKG : 0;
      detail.push({
        date: e.date, day: DAYS[dow(e.date)], shift: SHIFT_SHORT[e.shift] || e.shift, line: e.line,
        no: w.no, worker: w.name, workerKG, shiftKG, shiftVal, share, dollar: share * shiftVal,
        refKG: q && wc ? `${q}!${wc.kg}` : null,
        refShiftKG: q && cells.shiftKg ? `${q}!${cells.shiftKg}` : null,
        refShiftVal: q && cells.shiftDollar ? `${q}!${cells.shiftDollar}` : null,
        refDollar: q && wc ? `${q}!${wc.value}` : null,
      });
    });
  });

  const names = [...new Set(detail.map((d) => d.worker))];
  const noBy = {}; detail.forEach((d) => { if (noBy[d.worker] == null && d.no != null) noBy[d.worker] = d.no; });
  const dollarByDay = {};
  detail.forEach((d) => { (dollarByDay[d.worker] = dollarByDay[d.worker] || {}); dollarByDay[d.worker][d.date] = (dollarByDay[d.worker][d.date] || 0) + d.dollar; });
  const kgBy = {}; wk.forEach((e) => (e.workers || []).forEach((w) => { kgBy[w.name] = (kgBy[w.name] || 0) + num(w.earned_kg); }));
  const totalBy = {}; names.forEach((n) => { totalBy[n] = dates.reduce((s, d) => s + (dollarByDay[n]?.[d] || 0), 0); });
  names.sort((a, b) => totalBy[b] - totalBy[a]);

  // Matrix summary ('Weekly Pay') — Worker × Day
  const nDays = dates.length;
  const ms = wb.addWorksheet("Weekly Pay");
  const totalCol = 3 + nDays, kgCol = totalCol + 1, rateCol = totalCol + 2, lastCol = rateCol;
  ms.getCell("A1").value = `WEEKLY PAY — Week ${week} — $ earned per worker per day (ADMIN & MANAGER ONLY)`; title(ms.getCell("A1")); ms.mergeCells(1, 1, 1, lastCol);
  ms.getCell("A2").value = "Each day cell = that worker's $ from every shift that day (live SUMIFS into 'Weekly Pay Detail'). Weekly Total = sum of the days and matches the daily sheets. Worker rates, NOT charge-out."; ms.getCell("A2").font = { italic: true, size: 9, color: { argb: "FFB45309" } };
  const hr = ms.getRow(4); hr.values = ["No.", "Worker", ...dates.map((d) => shortDay(d)), "Total $", "KG earned", "$/kg"]; head(hr);
  const firstRow = 5; let mr = firstRow;
  names.forEach((n) => {
    const row = ms.getRow(mr);
    row.getCell(1).value = noBy[n] ?? "";
    row.getCell(2).value = n;
    dates.forEach((d, di) => {
      const col = 3 + di;
      row.getCell(col).value = { formula: `SUMIFS('Weekly Pay Detail'!$K:$K,'Weekly Pay Detail'!$F:$F,$B${mr},'Weekly Pay Detail'!$A:$A,"${d}")`, result: num(dollarByDay[n]?.[d] || 0) };
      fill(row.getCell(col), GREEN, MONEY);
    });
    const firstDayL = ms.getColumn(3).letter, lastDayL = ms.getColumn(2 + nDays).letter;
    const totalL = ms.getColumn(totalCol).letter, kgL = ms.getColumn(kgCol).letter;
    row.getCell(totalCol).value = { formula: `SUM(${firstDayL}${mr}:${lastDayL}${mr})`, result: num(totalBy[n]) }; fill(row.getCell(totalCol), YELLOW, MONEY); row.getCell(totalCol).font = { bold: true };
    row.getCell(kgCol).value = { formula: `SUMIF('Weekly Pay Detail'!$F:$F,$B${mr},'Weekly Pay Detail'!$G:$G)`, result: num(kgBy[n] || 0) }; fill(row.getCell(kgCol), GREEN, NUM);
    row.getCell(rateCol).value = { formula: `IFERROR(${totalL}${mr}/${kgL}${mr},0)`, result: kgBy[n] ? num(totalBy[n] / kgBy[n]) : 0 }; fill(row.getCell(rateCol), GREEN, MONEY);
    mr++;
  });
  const lastRow = mr - 1;
  const tr = ms.getRow(mr); tr.getCell(2).value = "DAILY TOTAL"; tr.font = { bold: true };
  dates.forEach((d, di) => { const col = 3 + di; const L = ms.getColumn(col).letter; tr.getCell(col).value = lastRow >= firstRow ? { formula: `SUM(${L}${firstRow}:${L}${lastRow})`, result: num(names.reduce((s, n) => s + (dollarByDay[n]?.[d] || 0), 0)) } : 0; fill(tr.getCell(col), YELLOW, MONEY); });
  const totL = ms.getColumn(totalCol).letter, kgLt = ms.getColumn(kgCol).letter;
  tr.getCell(totalCol).value = lastRow >= firstRow ? { formula: `SUM(${totL}${firstRow}:${totL}${lastRow})`, result: num(names.reduce((s, n) => s + totalBy[n], 0)) } : 0; fill(tr.getCell(totalCol), YELLOW, MONEY);
  tr.getCell(kgCol).value = lastRow >= firstRow ? { formula: `SUM(${kgLt}${firstRow}:${kgLt}${lastRow})`, result: num(names.reduce((s, n) => s + (kgBy[n] || 0), 0)) } : 0; fill(tr.getCell(kgCol), YELLOW, NUM);
  ms.getColumn(1).width = 7; ms.getColumn(2).width = 26; for (let c = 3; c <= lastCol; c++) ms.getColumn(c).width = 12;

  // Traceable detail ('Weekly Pay Detail') — one row per worker × shift
  const ds = wb.addWorksheet("Weekly Pay Detail");
  ds.getCell("A1").value = `WEEKLY PAY — TRACE DETAIL — Week ${week} (one row per worker × shift)`; title(ds.getCell("A1")); ds.mergeCells("A1:K1");
  ds.getCell("A2").value = "Worker $ = (worker KG ÷ shift KG) × shift $ — the same figure shown on each daily sheet. Green cells are live formulas."; ds.getCell("A2").font = { italic: true, size: 9, color: { argb: "FFB45309" } };
  const dh = ds.getRow(4); dh.values = ["Date", "Day", "Shift", "Line", "No.", "Worker", "Worker KG", "Shift KG", "Shift $", "Share %", "Worker $"]; head(dh);
  let dr = 5;
  detail.forEach((d) => {
    const row = ds.getRow(dr);
    row.getCell(1).value = d.date; row.getCell(2).value = d.day; row.getCell(3).value = d.shift; row.getCell(4).value = d.line;
    row.getCell(5).value = d.no ?? ""; row.getCell(6).value = d.worker;
    row.getCell(7).value = d.refKG ? { formula: d.refKG, result: num(d.workerKG) } : num(d.workerKG); fill(row.getCell(7), GREEN, NUM);
    row.getCell(8).value = d.refShiftKG ? { formula: d.refShiftKG, result: num(d.shiftKG) } : num(d.shiftKG); fill(row.getCell(8), GREEN, NUM);
    row.getCell(9).value = d.refShiftVal ? { formula: d.refShiftVal, result: num(d.shiftVal) } : num(d.shiftVal); fill(row.getCell(9), GREEN, MONEY);
    row.getCell(10).value = { formula: `IFERROR(G${dr}/H${dr},0)`, result: d.share }; row.getCell(10).numFmt = "0.0%"; fill(row.getCell(10), GREEN);
    row.getCell(11).value = d.refDollar ? { formula: d.refDollar, result: num(d.dollar) } : { formula: `J${dr}*I${dr}`, result: num(d.dollar) }; fill(row.getCell(11), GREEN, MONEY);
    dr++;
  });
  [12, 7, 8, 16, 7, 26, 11, 11, 12, 9, 12].forEach((w, i) => (ds.getColumn(i + 1).width = w));
}

function download(wb, name) {
  wb.xlsx.writeBuffer().then((buf) => {
    const url = URL.createObjectURL(new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }));
    const a = document.createElement("a"); a.href = url; a.download = name; a.click(); URL.revokeObjectURL(url);
  });
}

export async function exportShift(entry, money = false, embedPhotos = false) {
  const wb = new ExcelJS.Workbook();
  await addDailySheet(wb, entry.date, [entry], entry.week || "", money, embedPhotos);
  download(wb, `G&L Production - Week ${entry.week || ""} - ${shortDay(entry.date)} ${entry.line} ${entry.shift}.xlsx`);
}

export async function exportDaily(entries, date, money = false, embedPhotos = false) {
  const wb = new ExcelJS.Workbook();
  const wk = entries.find((e) => e.date === date)?.week || "";
  await addDailySheet(wb, date, entries, wk, money, embedPhotos);
  download(wb, `G&L Production - Week ${wk} - ${shortDay(date)}.xlsx`);
}

// Admin-only: one-tap worker earnings ranking for payroll / performance review.
export function exportWorkerEarnings(entries, week) {
  const wb = new ExcelJS.Workbook();
  const dates = weekDatesFrom(entries);
  const inWeek = new Set(dates);
  const wk = entries.filter((e) => inWeek.has(e.date));
  const by = new Map();
  wk.forEach((e) => (e.workers || []).forEach((w) => {
    const key = w.name;
    if (!by.has(key)) by.set(key, { no: w.no, name: w.name, kg: 0, val: 0, shifts: 0, minutes: 0, buckets: 0 });
    const r = by.get(key);
    if (r.no == null && w.no != null) r.no = w.no;
    r.kg += Number(w.earned_kg) || 0;
    r.val += Number(w.value) || 0;
    r.minutes += Number(w.minutes) || 0;
    r.buckets += Number(w.buckets) || 0;
    r.shifts += 1;
  }));
  const rows = [...by.values()].sort((a, b) => b.val - a.val);
  const ws = wb.addWorksheet("Worker Earnings");
  ws.columns = [{ width: 8 }, { width: 8 }, { width: 30 }, { width: 9 }, { width: 12 }, { width: 12 }, { width: 14 }, { width: 14 }];
  ws.getCell("A1").value = `WORKER EARNINGS — Week ${week} (${ddmm(dates[0])} to ${ddmm(dates[6])}) — ADMIN & MANAGER ONLY`;
  title(ws.getCell("A1")); ws.mergeCells("A1:H1");
  const hr = ws.getRow(3);
  hr.values = ["Rank", "No.", "Worker", "Shifts", "Minutes", "KG earned", "$ earned", "Avg $/shift"];
  hr.font = { bold: true, color: { argb: "FFFFFFFF" } };
  hr.eachCell((c) => fill(c, NAVY));
  let totalVal = 0, totalKg = 0, totalMin = 0;
  rows.forEach((r, i) => {
    const xr = ws.getRow(4 + i);
    xr.values = [i + 1, r.no ?? "", r.name, r.shifts, r.minutes || "", r.kg, r.val, r.shifts ? r.val / r.shifts : 0];
    xr.getCell(6).numFmt = NUM; xr.getCell(7).numFmt = MONEY; xr.getCell(8).numFmt = MONEY;
    totalVal += r.val; totalKg += r.kg; totalMin += r.minutes || 0;
  });
  const tr = ws.getRow(4 + rows.length + 1);
  tr.values = ["", "", "TOTAL", rows.length, totalMin || "", totalKg, totalVal, ""];
  tr.font = { bold: true }; tr.eachCell((c) => fill(c, YELLOW));
  tr.getCell(6).numFmt = NUM; tr.getCell(7).numFmt = MONEY;
  download(wb, `G&L Worker Earnings - Week ${week} — ADMIN ONLY.xlsx`);
}

// Admin/supervisor: one TAB PER WORKER — every shift they worked this week with Minutes,
// Buckets, KG earned and $ earned, each a LIVE formula linked to the daily sheets (bundled in
// this same file so the formulas resolve). Built to hand/send to a worker to prove performance.
export async function exportWorkerPerformance(entries, week, money = false) {
  const wb = new ExcelJS.Workbook();
  const dates = weekDatesFrom(entries);
  const inWeek = new Set(dates);
  const wk = entries.filter((e) => inWeek.has(e.date));
  // Build the daily sheets first so every worker cell can trace live back to them.
  for (const d of dates) await addDailySheet(wb, d, wk, week, money, false);
  addLineTallySheet(wb, wk, money);
  addDowntimeTrainingSheet(wb, wk);

  const byWorker = new Map();
  wk.forEach((e) => {
    const c = e.__cells; const q = c && c.sheet ? `'${c.sheet.replace(/'/g, "''")}'` : null;
    const wmap = {}; (c?.workers || []).forEach((wc) => { wmap[`${wc.no}|${wc.name}`] = wc; });
    (e.workers || []).forEach((w) => {
      const key = `${w.no}|${w.name}`;
      if (!byWorker.has(key)) byWorker.set(key, { no: w.no, name: w.name, shifts: [] });
      const wc = wmap[key];
      byWorker.get(key).shifts.push({
        date: e.date, day: DAYFULL[dow(e.date)], shift: SHIFT_LABEL[e.shift] || e.shift, line: e.line,
        min: Number(w.minutes) || 0, bkt: Number(w.buckets) || 0, kg: num(w.earned_kg), val: num(w.value),
        minRef: q && wc ? `${q}!${wc.minutes}` : null, bktRef: q && wc ? `${q}!${wc.buckets}` : null,
        kgRef: q && wc ? `${q}!${wc.kg}` : null, valRef: q && wc ? `${q}!${wc.value}` : null,
      });
    });
  });
  const workers = [...byWorker.values()].sort((a, b) => (a.no ?? 1e9) - (b.no ?? 1e9) || String(a.name).localeCompare(b.name));

  // Combined "All Workers" sheet — everyone's tallies in one place (handy when there are too many tabs).
  if (workers.length) {
    const aw = wb.addWorksheet("All Workers");
    aw.getCell("A1").value = `ALL WORKERS — WEEK ${week} PERFORMANCE (every worker's shifts in one sheet)`; title(aw.getCell("A1")); aw.mergeCells(money ? "A1:I1" : "A1:H1");
    aw.getCell("A2").value = "Live formulas linked to the daily sheets. Each worker's block ends with their weekly subtotal; grand total at the bottom."; aw.getCell("A2").font = { italic: true, size: 9 };
    const cols = money ? ["Worker", "Date", "Day", "Shift", "Line", "Minutes", "Buckets", "KG earned", "$ earned"] : ["Worker", "Date", "Day", "Shift", "Line", "Minutes", "Buckets", "KG earned"];
    const hr = aw.getRow(4); hr.values = cols; head(hr);
    let r = 5;
    const subRows = [];
    workers.forEach((wkr) => {
      wkr.shifts.sort((a, b) => a.date.localeCompare(b.date));
      const wname = `${wkr.no != null ? "#" + wkr.no + " " : ""}${wkr.name}`;
      const first = r;
      wkr.shifts.forEach((s) => {
        const row = aw.getRow(r);
        row.getCell(1).value = wname; row.getCell(2).value = s.date; row.getCell(3).value = s.day; row.getCell(4).value = s.shift; row.getCell(5).value = s.line;
        row.getCell(6).value = s.minRef ? { formula: s.minRef, result: s.min } : s.min; fill(row.getCell(6), GREEN, "0");
        row.getCell(7).value = s.bktRef ? { formula: s.bktRef, result: s.bkt } : s.bkt; fill(row.getCell(7), GREEN, "0");
        row.getCell(8).value = s.kgRef ? { formula: s.kgRef, result: s.kg } : s.kg; fill(row.getCell(8), GREEN, NUM);
        if (money) { row.getCell(9).value = s.valRef ? { formula: s.valRef, result: s.val } : s.val; fill(row.getCell(9), GREEN, MONEY); }
        r++;
      });
      const last = r - 1;
      const tMin = wkr.shifts.reduce((a, s) => a + s.min, 0), tBkt = wkr.shifts.reduce((a, s) => a + s.bkt, 0);
      const tKg = wkr.shifts.reduce((a, s) => a + s.kg, 0), tVal = wkr.shifts.reduce((a, s) => a + s.val, 0);
      const rng = (L, res) => last >= first ? { formula: `SUM(${L}${first}:${L}${last})`, result: num(res) } : num(res);
      const sub = aw.getRow(r); sub.getCell(1).value = `${wname} — total`; sub.font = { bold: true };
      sub.getCell(6).value = rng("F", tMin); fill(sub.getCell(6), YELLOW, "0");
      sub.getCell(7).value = rng("G", tBkt); fill(sub.getCell(7), YELLOW, "0");
      sub.getCell(8).value = rng("H", tKg); fill(sub.getCell(8), YELLOW, NUM);
      if (money) { sub.getCell(9).value = rng("I", tVal); fill(sub.getCell(9), YELLOW, MONEY); }
      subRows.push(r); r += 1;
    });
    const gMin = workers.reduce((a, w) => a + w.shifts.reduce((s, x) => s + x.min, 0), 0);
    const gBkt = workers.reduce((a, w) => a + w.shifts.reduce((s, x) => s + x.bkt, 0), 0);
    const gKg = workers.reduce((a, w) => a + w.shifts.reduce((s, x) => s + x.kg, 0), 0);
    const gVal = workers.reduce((a, w) => a + w.shifts.reduce((s, x) => s + x.val, 0), 0);
    const gsum = (L, res) => subRows.length ? { formula: subRows.map((x) => `${L}${x}`).join("+"), result: num(res) } : num(res);
    const gt = aw.getRow(r); gt.getCell(1).value = "GRAND TOTAL"; gt.font = { bold: true, size: 12 };
    gt.getCell(6).value = gsum("F", gMin); fill(gt.getCell(6), YELLOW, "0");
    gt.getCell(7).value = gsum("G", gBkt); fill(gt.getCell(7), YELLOW, "0");
    gt.getCell(8).value = gsum("H", gKg); fill(gt.getCell(8), YELLOW, NUM);
    if (money) { gt.getCell(9).value = gsum("I", gVal); fill(gt.getCell(9), YELLOW, MONEY); }
    [20, 12, 11, 20, 18, 10, 10, 12, 12].forEach((w, i) => (aw.getColumn(i + 1).width = w));
  }

  const used = new Set();
  workers.forEach((wkr) => {
    wkr.shifts.sort((a, b) => a.date.localeCompare(b.date));
    let nm = `#${wkr.no ?? "?"} ${wkr.name}`.replace(/[\\/?*[\]:]/g, " ").slice(0, 31).trim();
    const base = nm; let i = 2; while (used.has(nm)) { nm = `${base.slice(0, 27)} ${i}`.trim(); i++; } used.add(nm);
    const ws = wb.addWorksheet(nm);
    ws.getCell("A1").value = `${wkr.no != null ? "#" + wkr.no + " " : ""}${wkr.name} — WEEK ${week} PERFORMANCE`; title(ws.getCell("A1"));
    ws.getCell("A2").value = "Every number below is a LIVE formula linked to the daily sheets in this file — click a green cell to trace it back to the day it was logged."; ws.getCell("A2").font = { italic: true, size: 9 };
    const cols = money ? ["Date", "Day", "Shift", "Line", "Minutes", "Buckets", "KG earned", "$ earned"] : ["Date", "Day", "Shift", "Line", "Minutes", "Buckets", "KG earned"];
    const hr = ws.getRow(4); hr.values = cols; head(hr);
    let r = 5; const first = r;
    wkr.shifts.forEach((s) => {
      const row = ws.getRow(r);
      row.getCell(1).value = s.date; row.getCell(2).value = s.day; row.getCell(3).value = s.shift; row.getCell(4).value = s.line;
      row.getCell(5).value = s.minRef ? { formula: s.minRef, result: s.min } : s.min; fill(row.getCell(5), GREEN, "0");
      row.getCell(6).value = s.bktRef ? { formula: s.bktRef, result: s.bkt } : s.bkt; fill(row.getCell(6), GREEN, "0");
      row.getCell(7).value = s.kgRef ? { formula: s.kgRef, result: s.kg } : s.kg; fill(row.getCell(7), GREEN, NUM);
      if (money) { row.getCell(8).value = s.valRef ? { formula: s.valRef, result: s.val } : s.val; fill(row.getCell(8), GREEN, MONEY); }
      r++;
    });
    const last = r - 1;
    const totMin = wkr.shifts.reduce((a, s) => a + s.min, 0), totBkt = wkr.shifts.reduce((a, s) => a + s.bkt, 0);
    const totKg = wkr.shifts.reduce((a, s) => a + s.kg, 0), totVal = wkr.shifts.reduce((a, s) => a + s.val, 0);
    const rng = (L, res) => last >= first ? { formula: `SUM(${L}${first}:${L}${last})`, result: num(res) } : num(res);
    const tr = ws.getRow(r); tr.getCell(4).value = "WEEK TOTAL"; tr.font = { bold: true };
    tr.getCell(5).value = rng("E", totMin); fill(tr.getCell(5), YELLOW, "0");
    tr.getCell(6).value = rng("F", totBkt); fill(tr.getCell(6), YELLOW, "0");
    tr.getCell(7).value = rng("G", totKg); fill(tr.getCell(7), YELLOW, NUM);
    if (money) { tr.getCell(8).value = rng("H", totVal); fill(tr.getCell(8), YELLOW, MONEY); }
    r += 2;
    ws.getCell(`A${r}`).value = `Shifts worked this week: ${wkr.shifts.length}`; ws.getCell(`A${r}`).font = { bold: true, size: 10 };
    [12, 11, 22, 18, 10, 10, 12, 12].forEach((w, i) => (ws.getColumn(i + 1).width = w));
  });
  if (!workers.length) { const ws = wb.addWorksheet("No data"); ws.getCell("A1").value = `No worker activity for Week ${week}.`; }
  download(wb, `G&L Worker Performance - Week ${week}${money ? " — ADMIN ONLY" : ""}.xlsx`);
}

async function addPhotoSheet(wb, entries, embedPhotos = false) {
  const withPhotos = entries.filter((e) => (e.photos || []).length);
  if (!withPhotos.length) return;
  const ws = wb.addWorksheet("Photo Evidence");
  ws.getCell("A1").value = embedPhotos
    ? "PHOTO EVIDENCE — scale / count-sheet photos attached to each shift"
    : "PHOTO EVIDENCE — click a link to open the scale / count-sheet photo or document"; title(ws.getCell("A1"));
  ws.getColumn(1).width = 46; ws.getColumn(2).width = 40;
  let r = 3;
  for (const e of withPhotos) {
    ws.getCell(`A${r}`).value = `${shortDay(e.date)} · ${e.line} — ${SHIFT_LABEL[e.shift] || e.shift}`; section(ws.getCell(`A${r}`)); r++;
    ws.getCell(`A${r}`).value = `Logged by ${e.saved_by_name || "—"}${e.saved_at ? " · " + new Date(e.saved_at).toLocaleString("en-AU") : ""}`;
    ws.getCell(`A${r}`).font = { italic: true, size: 9, color: { argb: "FF777777" } }; r += 1;
    for (const p of (e.photos || [])) {
      if (embedPhotos) {
        const path = p.storage_path || p.filename || "";
        try {
          const res = await api.get(`/files/${path}`, { responseType: "arraybuffer" });
          const ext0 = (path.split(".").pop() || "jpeg").toLowerCase();
          const extension = ext0 === "jpg" ? "jpeg" : (["png", "gif", "jpeg"].includes(ext0) ? ext0 : "jpeg");
          const imageId = wb.addImage({ buffer: res.data, extension });
          ws.getCell(`B${r}`).value = p.original_filename || p.filename || "photo";
          ws.getCell(`B${r}`).font = { size: 9, color: { argb: "FF777777" } };
          ws.addImage(imageId, { tl: { col: 0, row: r - 1 }, ext: { width: 280, height: 210 } });
          for (let k = 0; k < 11; k++) ws.getRow(r + k).height = 16;
          r += 12;
        } catch (err) {
          linkCell(ws.getCell(`A${r}`), `📎 ${p.original_filename || p.filename || "open file"} (open in browser)`, photoUrl(p)); r += 1;
        }
      } else {
        linkCell(ws.getCell(`A${r}`), `📷 ${p.original_filename || p.filename || "open photo / document"}`, photoUrl(p)); r += 1;
      }
    }
    r += 1;
  }
}

async function addPricingSheet(wb) {
  let data = null;
  try { const res = await api.get("/line-rates"); data = res.data; } catch { return; }
  if (!data || !data.lines) return;
  const ws = wb.addWorksheet("Line Pricing");
  ws.getCell("A1").value = "LINE PRICING — $/kg by line & shift category (ADMIN ONLY). Blue cells are editable — adjust rates here."; title(ws.getCell("A1")); ws.mergeCells("A1:D1");
  ws.getCell("A2").value = "Column A = 1st (Night) shift weekday.  Column B = 2nd, 3rd shift & Pooraka Day.  Column C = Saturday, Sunday & Public Holiday. Product W7324 kilos are excluded from the payable value."; ws.getCell("A2").font = { italic: true, size: 9 };
  const hr = ws.getRow(4);
  hr.values = ["Line", "A: 1st (Night)  $/kg", "B: 2nd / 3rd / Pooraka  $/kg", "C: Sat / Sun / PH  $/kg"]; head(hr);
  let r = 5;
  data.lines.forEach((l) => {
    const rate = data.rates[l] || { a: 0, b: 0, c: 0 };
    const row = ws.getRow(r);
    row.getCell(1).value = l;
    row.getCell(2).value = rate.a ?? ""; fill(row.getCell(2), BLUE, MONEY);
    row.getCell(3).value = rate.b ?? ""; fill(row.getCell(3), BLUE, MONEY);
    row.getCell(4).value = rate.c ?? ""; fill(row.getCell(4), BLUE, MONEY);
    r++;
  });
  [26, 22, 26, 24].forEach((w, i) => (ws.getColumn(i + 1).width = w));
}

function addDowntimeTrainingSheet(wb, entries) {
  const dt = [], tr = [];
  (entries || []).forEach((e) => {
    (e.workers || []).forEach((w) => { if (Number(w.downtime_min) > 0) dt.push({ date: e.date, worker: `${w.no != null ? "#" + w.no + " " : ""}${w.name || ""}`, min: Number(w.downtime_min) || 0, reason: w.downtime_reason || "" }); });
    (e.training || []).forEach((t) => { if (t.trainer || t.trainee || Number(t.minutes) > 0) tr.push({ date: e.date, trainer: t.trainer || "", trainee: t.trainee || "", min: Number(t.minutes) || 0, reason: t.reason || "" }); });
  });
  if (!dt.length && !tr.length) return;
  const ws = wb.addWorksheet("Downtime & Training");
  ws.getCell("A1").value = "DOWNTIME & TRAINING — recorded for manual charge / pay"; title(ws.getCell("A1")); ws.mergeCells("A1:E1");
  let r = 3;
  ws.getCell(`A${r}`).value = "DOWNTIME"; ws.getCell(`A${r}`).font = { bold: true }; r++;
  { const h = ws.getRow(r); h.values = ["Date", "Worker", "Minutes", "Reason"]; head(h); r++; }
  dt.forEach((d) => { const x = ws.getRow(r); x.values = [d.date, d.worker, d.min, d.reason]; fill(x.getCell(3), BLUE, "0"); r++; });
  { const t = ws.getRow(r); t.getCell(2).value = "Total downtime min"; t.font = { bold: true }; t.getCell(3).value = dt.reduce((a, b) => a + b.min, 0); fill(t.getCell(3), YELLOW, "0"); r += 2; }
  ws.getCell(`A${r}`).value = "TRAINING"; ws.getCell(`A${r}`).font = { bold: true }; r++;
  { const h = ws.getRow(r); h.values = ["Date", "Trainer", "Trainee", "Minutes", "Reason"]; head(h); r++; }
  tr.forEach((t) => { const x = ws.getRow(r); x.values = [t.date, t.trainer, t.trainee, t.min, t.reason]; fill(x.getCell(4), BLUE, "0"); r++; });
  { const t = ws.getRow(r); t.getCell(3).value = "Total training min"; t.font = { bold: true }; t.getCell(4).value = tr.reduce((a, b) => a + b.min, 0); fill(t.getCell(4), YELLOW, "0"); }
  [12, 22, 18, 12, 30].forEach((w, i) => (ws.getColumn(i + 1).width = w));
}

function lineTotals(entries) {
  const by = new Map();
  (entries || []).forEach((e) => {
    const l = e.line || "—";
    if (!by.has(l)) by.set(l, { line: l, kg: 0, value: 0 });
    const r = by.get(l);
    r.kg += Number(e.total_worked) || 0;
    r.value += Number(e.production_value) || 0;
  });
  return [...by.values()].sort((a, b) => b.kg - a.kg);
}

// "Kilos by line" tally — total worker kilos (and $ for admins) produced per line this week.
function addLineTallySheet(wb, entries, money) {
  const ws = wb.addWorksheet("Kilos by Line");
  ws.getCell("A1").value = "KILOS BY LINE — total produced per line this week (worker kilos)"; title(ws.getCell("A1")); ws.mergeCells(money ? "A1:C1" : "A1:B1");
  const hr = ws.getRow(3); hr.values = money ? ["Line", "Week kg", "Value $"] : ["Line", "Week kg"]; head(hr);
  const rows = lineTotals(entries);
  let r = 4; const first = r;
  rows.forEach((row) => {
    const x = ws.getRow(r);
    x.getCell(1).value = row.line;
    x.getCell(2).value = num(row.kg); fill(x.getCell(2), GREEN, NUM);
    if (money) { x.getCell(3).value = num(row.value); fill(x.getCell(3), GREEN, MONEY); }
    r++;
  });
  const last = r - 1;
  const tr = ws.getRow(r); tr.getCell(1).value = "TOTAL"; tr.font = { bold: true };
  tr.getCell(2).value = last >= first ? { formula: `SUM(B${first}:B${last})`, result: num(rows.reduce((a, b) => a + b.kg, 0)) } : 0; fill(tr.getCell(2), YELLOW, NUM);
  if (money) { tr.getCell(3).value = last >= first ? { formula: `SUM(C${first}:C${last})`, result: num(rows.reduce((a, b) => a + b.value, 0)) } : 0; fill(tr.getCell(3), YELLOW, MONEY); }
  const l1 = (entries || []).reduce((a, e) => a + (Number(e.line1_kg) || 0), 0);
  const l2 = (entries || []).reduce((a, e) => a + (Number(e.line2_kg) || 0), 0);
  if (l1 > 0 || l2 > 0) {
    r += 1;
    const a1 = ws.getRow(r); a1.getCell(1).value = "Line 1 — tally only (NOT pooled)"; a1.getCell(2).value = num(l1); fill(a1.getCell(2), BLUE, NUM); r++;
    const a2 = ws.getRow(r); a2.getCell(1).value = "Line 2 — pooled + tallied"; a2.getCell(2).value = num(l2); fill(a2.getCell(2), BLUE, NUM); r++;
  }
  [28, 14, 14].forEach((w, i) => (ws.getColumn(i + 1).width = w));
}

export async function exportWeekly(entries, week, money = false, embedPhotos = false) {
  const wb = new ExcelJS.Workbook();
  const dates = weekDatesFrom(entries);
  // Defensive: only include entries that fall inside this Sun→Sat window so a stray
  // out-of-week entry can never silently drop/corrupt a sheet (root-cause guard).
  const inWeek = new Set(dates);
  const wkEntries = entries.filter((e) => inWeek.has(e.date));
  for (const d of dates) await addDailySheet(wb, d, wkEntries, week, money, embedPhotos);
  addWeeklySummary(wb, wkEntries, dates, week, money);
  addLineTallySheet(wb, wkEntries, money);
  addDowntimeTrainingSheet(wb, wkEntries);
  addWeeklyProductTally(wb, wkEntries, dates, week);
  if (money) addWeeklyPay(wb, wkEntries, dates, week);
  if (money) await addPricingSheet(wb);
  await addPhotoSheet(wb, wkEntries, embedPhotos);
  const yr = (iso) => parse(iso).getFullYear();
  download(wb, `G&L Production - Week ${week} (${ddmm(dates[0])}-${yr(dates[0])} to ${ddmm(dates[6])}-${yr(dates[6])}).xlsx`);
}

export function exportKPI(kpi, week) {
  const wb = new ExcelJS.Workbook();
  const ws = wb.addWorksheet(`KPI Week ${week}`);
  ws.getCell("A1").value = `KPI — WEEK ${week} — PRODUCTION VALUE`; title(ws.getCell("A1"));
  ws.getCell("A2").value = "KPI ONLY — NEVER PAYROLL. Charge-out $/kg × allocated kg (admin & manager only)."; ws.getCell("A2").font = { bold: true, color: { argb: "FFB45309" } };
  const s = ws.getRow(4); s.values = ["Total kg", num(kpi.total_kg), "Production value", num(kpi.production_value)]; s.font = { bold: true };
  fill(s.getCell(2), GREEN, NUM); fill(s.getCell(4), YELLOW, MONEY);
  const a = ws.getRow(5); a.values = ["Avg $/kg", num(kpi.avg_per_kg), "Shifts", kpi.shifts]; fill(a.getCell(2), GREEN, MONEY);
  const th = ws.getRow(7); th.values = ["Top product", "kg", "Value $"]; head(th);
  let r = 8;
  (kpi.top_products || []).forEach((p) => { const row = ws.getRow(r++); row.values = [p.code, num(p.kg), num(p.value)]; fill(row.getCell(2), GREEN, NUM); fill(row.getCell(3), GREEN, MONEY); });
  ws.getColumn(1).width = 20; for (let c = 2; c <= 4; c++) ws.getColumn(c).width = 16;
  download(wb, `G&L KPI - Week ${week} — ADMIN & MANAGER ONLY.xlsx`);
}
